// client/src/pages/secure/VirtualOfficeSettingsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import PageHeader, { AboutThisPage } from '../../components/shared/PageHeader';

const FIELDS = [
    { key: 'brokerage_name', label: 'Brokerage Legal Name (printed on generated documents)', placeholder: 'e.g. Business Real Estate Corp', type: 'text' },
    { key: 'ein', label: 'Federal EIN (tax filings — 941, 940, W-2, 1120-S)', placeholder: 'e.g. 12-3456789', type: 'text' },
    { key: 'ides_account', label: 'IDES Unemployment Account # (Illinois SUTA)', placeholder: 'e.g. 5101294', type: 'text' },
    { key: 'public_phone_main', label: 'Website — main phone (public, footer and home page)', placeholder: 'e.g. (773) 762-4759', type: 'text' },
    { key: 'public_phone_secondary', label: 'Website — secondary phone (public; leave blank for none)', placeholder: 'e.g. (773) 205-5493', type: 'text' },
    { key: 'public_email', label: 'Website — email (public)', placeholder: 'e.g. info@bizrec.net', type: 'text' },
    { key: 'public_email_office', label: 'Website — office email (public; leave blank for none)', placeholder: 'e.g. office@businessrealestatecorp.com', type: 'text' },
    { key: 'service_area', label: 'Areas Served (public)', placeholder: 'e.g. Chicago and the greater Chicago metropolitan area, Illinois', type: 'text' },
    { key: 'sponsoring_broker_license_number', label: 'Sponsoring Broker License # (public)', placeholder: 'e.g. 478.011990', type: 'text' },
    { key: 'virtual_office_url', label: 'Website address (public — printed on advertising, the registry and OMs)', placeholder: 'https://businessrealestatecorp.com', type: 'text' },
    { key: 'brokerage_registered_in_state', label: 'Brokerage registered in this site\'s state? (Florida: type "yes" only once DBPR has registered the company — until then the website never says "licensed in Florida")', placeholder: 'no', type: 'text' },
    { key: 'virtual_office_license_number', label: 'Virtual Office License # (public, once issued by IDFPR)', placeholder: 'e.g. 495.xxxxxx', type: 'text' },
    { key: 'physical_address_street', label: 'Physical IL Address — Street (for service of process, not public)', placeholder: 'e.g. 4507 W Lawrence Ave', type: 'text' },
    { key: 'physical_address_city', label: 'City', placeholder: 'e.g. Chicago', type: 'text' },
    { key: 'physical_address_state', label: 'State', placeholder: 'e.g. Illinois', type: 'text' },
    { key: 'physical_address_zip', label: 'ZIP', placeholder: 'e.g. 60630', type: 'text' },
    // O8 (audit 2026-10): what the public footer and home page show on this site.
    { key: 'public_broker_name', label: 'Public website — Designated Managing Broker name (footer and home page)', placeholder: 'e.g. Alex George', type: 'text' },
    { key: 'public_broker_license', label: 'Public website — Managing Broker License #', placeholder: 'e.g. 471.001905', type: 'text' },
    { key: 'public_service_address', label: 'Public website — physical address for service of process (shown publicly)', placeholder: 'e.g. 4507 W Lawrence Ave., Chicago, IL 60630', type: 'text' },
];

const VirtualOfficeSettingsPage = () => {
    const [values, setValues] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const apiClient = useApiClient();

    const fetchSettings = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/brokerage-settings')
            .then(data => setValues(data))
            .catch(err => setError(err.message || 'Failed to load settings.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (key, value) => {
        setValues(prev => ({ ...prev, [key]: value }));
        setSaved(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');
        setSaved(false);
        try {
            const updated = await apiClient.put('/api/brokerage-settings', values);
            setValues(updated);
            setSaved(true);
        } catch (err) {
            setError(err.message || 'Failed to save settings.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="p-8">Loading virtual office settings...</div>;

    return (
        <div className="space-y-6">
            <div>
                <PageHeader title="Virtual Office Compliance Settings" className="!mb-3"
                    description="The business facts the Illinois virtual-office rules and IDFPR form 495 require." />
                <AboutThisPage className="!mb-0">
                    <p>These are the real business facts required by Illinois virtual-office rules (68 Ill. Adm. Code 1450.610) and IDFPR
                    registration form 495. Fields marked "public" appear on the <strong>Our Agents</strong> page at businessrealestatecorp.com/office-registry.
                    The physical address is used only for the actual IDFPR filing and service of process &mdash; it is not displayed publicly.</p>
                </AboutThisPage>
            </div>

            <form onSubmit={handleSave} className="bg-white p-4 sm:p-8 rounded-lg shadow-md space-y-6 max-w-2xl">
                {FIELDS.map(field => (
                    <div key={field.key}>
                        <label htmlFor={field.key} className="block text-sm font-medium text-slate-700">{field.label}</label>
                        <input
                            id={field.key}
                            type="text"
                            value={values[field.key] || ''}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                        />
                    </div>
                ))}

                {error && <p className="text-red-500 text-sm">{error}</p>}
                {saved && <p className="text-green-600 text-sm">Saved.</p>}

                <div className="flex justify-end">
                    <button type="submit" disabled={isSaving} className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-400">
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VirtualOfficeSettingsPage;
